import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MembershipCard from '../components/MembershipCard';
import QuantitySelector from '../components/QuantitySelector';
import MockCardFields from '../components/MockCardFields';
import { MEMBERSHIP_TIERS, getMembershipTier, formatUsd } from '../config/membership';
import { createMembershipCheckout } from '../utils/api';
import { trackEvent, getStoredUtms } from '../utils/tracking';
import './Membership.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_TIER_KEY = MEMBERSHIP_TIERS.find((t) => t.popular)?.key || MEMBERSHIP_TIERS[0].key;

// Real Stripe Subscriptions aren't wired yet (functions/_lib/stripe.js createSubscription throws
// outside mock mode), so — unlike Checkout.jsx — there's no real-card branch here yet.
export default function Membership() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paramTier = searchParams.get('tier');
  const initialKey = paramTier && getMembershipTier(paramTier) ? paramTier : DEFAULT_TIER_KEY;
  const [selectedTierKey, setSelectedTierKey] = useState(initialKey);
  const [quantity, setQuantity] = useState(1);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [session, setSession] = useState(null); // { membershipId, tier, subscriptionId, clientSecret, amountCents, mock }
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const selectedTier = getMembershipTier(selectedTierKey);
  const totalCents = selectedTier.priceCents * quantity;

  const handleSelectTier = (tierKey) => {
    setSelectedTierKey(tierKey);
  };

  const handleQuantityChange = (next) => {
    setQuantity(next);
    trackEvent('Membership Quantity Changed', { quantity: next });
  };

  const handleContinueToPayment = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setEmailError("That email doesn't look quite right.");
      return;
    }
    setEmailError(null);
    setSubmitting(true);
    setPaymentError(null);
    trackEvent('Membership Checkout Started', { tier: selectedTierKey, quantity, revenue: totalCents / 100 });
    try {
      const result = await createMembershipCheckout({
        tier: selectedTierKey,
        quantity,
        email,
        utms: getStoredUtms(),
      });
      setSession(result);
    } catch (err) {
      setPaymentError("Something hiccuped on our end — your card wasn't charged. Try again?");
      trackEvent('Error Occurred', { step: 'membership-checkout-session', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubscribe = async () => {
    setSubmitting(true);
    setPaymentError(null);
    try {
      await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/membership/dev-simulate-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          membershipId: session.membershipId,
          subscriptionId: session.subscriptionId,
          tier: session.tier,
          email,
          quantity,
          amountCents: session.amountCents,
          utms: getStoredUtms(),
        }),
      });
      navigate(`/membership/confirmation?membership=${session.membershipId}`);
    } catch (err) {
      setPaymentError("Something hiccuped on our end — your card wasn't charged. Try again?");
      trackEvent('Error Occurred', { step: 'membership-payment', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="tp-membership-page">
      <Header />
      <div className="container tp-membership-page__intro">
        <h1 className="headline-1">Travel+ Annual</h1>
        <p className="body-l tp-membership-page__subhead">
          Three ways to cover a year of trips. Pick the one that matches how often you travel.
        </p>
      </div>

      <div className="container tp-membership-page__grid">
        {MEMBERSHIP_TIERS.map((tier) => (
          <MembershipCard
            key={tier.key}
            tier={tier}
            selected={selectedTierKey === tier.key}
            onSelect={handleSelectTier}
          />
        ))}
      </div>

      <div className="container tp-membership-page__form-wrap">
        <div className="card tp-membership-page__form">
          <QuantitySelector quantity={quantity} onChange={handleQuantityChange} />
          <p className="body-m tp-membership-page__total">
            {quantity} × {formatUsd(selectedTier.priceCents)}/year ({selectedTier.name}) — <strong>{formatUsd(totalCents)}/year total</strong>
          </p>

          <div className="tp-membership-page__field">
            <label className="label-m" htmlFor="tp-membership-email">Where should we send everything?</label>
            <input
              id="tp-membership-email"
              type="email"
              className="input tp-membership-page__email"
              value={email}
              disabled={!!session}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
            {emailError && <p className="body-s tp-membership-page__error">{emailError}</p>}
          </div>

          {!session ? (
            <button
              type="button"
              className="btn btn-primary tp-membership-page__submit"
              onClick={handleContinueToPayment}
              disabled={submitting}
            >
              {submitting ? 'One sec…' : 'Continue to payment'}
            </button>
          ) : (
            <>
              <MockCardFields />
              {paymentError && <p className="body-s tp-membership-page__error">{paymentError}</p>}
              <button
                type="button"
                className="btn btn-primary tp-membership-page__submit"
                onClick={handleSubscribe}
                disabled={submitting}
              >
                {submitting ? 'Processing…' : `Subscribe — ${formatUsd(totalCents)}/year`}
              </button>
              <p className="body-s tp-membership-page__reassurance">
                Cancel anytime. Already-activated trips aren't refundable.
              </p>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
